import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  where,
  getDoc,
  setDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase/config.js';

class FirebaseDataService {
  constructor() {
    this.collections = {
      RECIPES: 'recipes',
      CATEGORIES: 'categories', 
      FAVORITES: 'favorites'
    };
  }

  // ================= RECIPES =================
  async getRecipes() {
    try {
      const recipesRef = collection(db, this.collections.RECIPES);
      const q = query(recipesRef, orderBy('datePublication', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return Object.assign({ id: doc.id }, data);
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des recettes:', error);
      return [];
    }
  }

  async getRecipe(id) {
    try {
      const docRef = doc(db, this.collections.RECIPES, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return Object.assign({ id: docSnap.id }, data);
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération de la recette:', error);
      return null;
    }
  }

  async addRecipe(recipeData) {
    try {
      const recipeToSave = Object.assign({
        datePublication: recipeData.datePublication || new Date().toISOString(),
        published: recipeData.published || true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }, recipeData);
      
      const docRef = await addDoc(collection(db, this.collections.RECIPES), recipeToSave);
      
      return Object.assign({ id: docRef.id }, recipeData);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la recette:', error);
      throw error;
    }
  }

  async updateRecipe(id, recipeData) {
    try {
      const docRef = doc(db, this.collections.RECIPES, id);
      await updateDoc(docRef, Object.assign({
        updatedAt: new Date().toISOString()
      }, recipeData));
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la recette:', error);
      throw error;
    }
  }

  async deleteRecipe(id) {
    try {
      await deleteDoc(doc(db, this.collections.RECIPES, id));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la recette:', error);
      throw error;
    }
  }

  // ================= CATEGORIES =================
  async getCategories() {
    try {
      const categoriesRef = collection(db, this.collections.CATEGORIES);
      const querySnapshot = await getDocs(categoriesRef);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return Object.assign({ id: doc.id }, data);
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      return [];
    }
  }

  async addCategory(categoryData) {
    try {
      const categoryToSave = Object.assign({
        createdAt: new Date().toISOString()
      }, categoryData);
      
      const docRef = await addDoc(collection(db, this.collections.CATEGORIES), categoryToSave);
      
      return Object.assign({ id: docRef.id }, categoryData);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la catégorie:', error);
      throw error;
    }
  }

  async updateCategory(id, categoryData) {
    try {
      const docRef = doc(db, this.collections.CATEGORIES, id);
      await updateDoc(docRef, Object.assign({
        updatedAt: new Date().toISOString()
      }, categoryData));
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la catégorie:', error);
      throw error;
    }
  }

  async deleteCategory(id) {
    try {
      await deleteDoc(doc(db, this.collections.CATEGORIES, id));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error);
      throw error;
    }
  }

  // ================= FAVORITES =================
  async getFavoriteRecipes() {
    try {
      const favoritesRef = doc(db, this.collections.FAVORITES, 'list');
      const docSnap = await getDoc(favoritesRef);
      
      if (docSnap.exists()) {
        const favoriteIds = docSnap.data().recipeIds || [];
        
        // Récupérer les objets complets des recettes favorites
        const favoriteRecipes = [];
        for (const recipeId of favoriteIds) {
          const recipe = await this.getRecipe(recipeId);
          if (recipe) {
            favoriteRecipes.push(recipe);
          }
        }
        return favoriteRecipes;
      }
      
      return [];
    } catch (error) {
      console.error('Erreur lors de la récupération des favoris:', error);
      return [];
    }
  }

  async addFavoriteRecipe(recipeId) {
    try {
      const favoritesRef = doc(db, this.collections.FAVORITES, 'list');
      const docSnap = await getDoc(favoritesRef);
      
      let currentIds = [];
      if (docSnap.exists()) {
        currentIds = docSnap.data().recipeIds || [];
      }
      
      if (!currentIds.includes(recipeId)) {
        currentIds.push(recipeId);
        await setDoc(favoritesRef, { 
          recipeIds: currentIds,
          updatedAt: new Date().toISOString()
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Erreur lors de l\'ajout aux favoris:', error);
      throw error;
    }
  }

  async removeFavoriteRecipe(recipeId) {
    try {
      const favoritesRef = doc(db, this.collections.FAVORITES, 'list');
      const docSnap = await getDoc(favoritesRef);
      
      if (docSnap.exists()) {
        const currentIds = docSnap.data().recipeIds || [];
        const filteredIds = currentIds.filter(id => id !== recipeId);
        
        await setDoc(favoritesRef, { 
          recipeIds: filteredIds,
          updatedAt: new Date().toISOString()
        });
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Erreur lors de la suppression des favoris:', error);
      throw error;
    }
  }

  async reorderFavorites(favoriteRecipes) {
    try {
      const recipeIds = favoriteRecipes.map(recipe => recipe.id);
      const favoritesRef = doc(db, this.collections.FAVORITES, 'list');
      
      await setDoc(favoritesRef, { 
        recipeIds: recipeIds,
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la réorganisation des favoris:', error);
      throw error;
    }
  }

  // ================= STORAGE (PHOTOS) =================
  async uploadPhoto(file, fileName) {
    try {
      const imageRef = ref(storage, `images/${fileName}`);
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        fileName: fileName,
        url: downloadURL,
        path: snapshot.ref.fullPath
      };
    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'image:', error);
      throw error;
    }
  }

  async deletePhoto(fileName) {
    try {
      const imageRef = ref(storage, `images/${fileName}`);
      await deleteObject(imageRef);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'image:', error);
      throw error;
    }
  }

  // ================= UTILITY =================
  async initializeData() {
    // Plus besoin d'initialisation spécifique avec Firebase
    console.log('Firebase Data Service initialisé');
  }
}

export default new FirebaseDataService();